<div class="strategy-detail-container">
  <!-- LOADING STATE -->
  <div *ngIf="isLoading" class="loading-state card-style">
    <div class="loading-content">
      <p-progressSpinner 
        strokeWidth="4" 
        animationDuration="1s"
        [style]="{ width: '60px', height: '60px' }">
      </p-progressSpinner>
      <p>Cargando estrategia...</p>
    </div>
  </div>

  <!-- ERROR STATE -->
  <div *ngIf="hasError && !isLoading" class="no-data-container">
    <div class="no-data-card">
      <i class="pi pi-exclamation-triangle" style="font-size: 3rem; color: #d21313;"></i>
      <h3>Error al cargar la estrategia</h3>
      <p>{{ errorMessage }}</p>
      <p-button 
        label="Volver" 
        icon="pi pi-arrow-left" 
        (onClick)="goBack()"
        styleClass="p-button-secondary">
      </p-button>
    </div>
  </div>

  <!-- CONTENT STATE -->
  <ng-container *ngIf="!isLoading && !hasError && strategy">
    <!-- HEADER PRINCIPAL -->
    <div class="card-style strategy-main-header">
      <div class="strategy-title-row">
        <div class="strategy-identity">
          <div class="strategy-icon" [style.background]="'linear-gradient(135deg, rgba(' + getScenarioColor(strategy.scenario) + ', 0.15) 0%, rgba(' + getScenarioColor(strategy.scenario) + ', 0.08) 100%)'">
            <i class="pi" [ngClass]="getScenarioIcon(strategy.scenario)" [style.color]="getScenarioColor(strategy.scenario)"></i>
          </div>
          
          <div class="strategy-title-content">
            <h1 class="strategy-name">{{ strategy.name }}</h1>
          </div>
        </div>

        <div class="strategy-actions">
          <p-button 
            label="Volver" 
            icon="pi pi-arrow-left" 
            (onClick)="goBack()"
            styleClass="p-button-outlined p-button-secondary">
          </p-button>
          <p-button 
            label="Editar" 
            icon="pi pi-pencil"
            (onClick)="editStrategy()"
            styleClass="p-button-primary">
          </p-button>
        </div>
      </div>

      <div class="strategy-status-row">
        <div class="strategy-status-badge" [ngClass]="strategy.active ? 'status-active' : 'status-inactive'">
          <i [class]="strategy.active ? 'pi pi-check-circle' : 'pi pi-times-circle'"></i>
          <span>{{ strategy.active ? 'Activa' : 'Inactiva' }}</span>
        </div>

        <div class="scenario-badge" [style.background]="'linear-gradient(135deg, rgba(' + getScenarioColor(strategy.scenario) + ', 0.15) 0%, rgba(' + getScenarioColor(strategy.scenario) + ', 0.08) 100%)'" [style.border-color]="getScenarioColor(strategy.scenario)">
          <i class="pi" [ngClass]="getScenarioIcon(strategy.scenario)" [style.color]="getScenarioColor(strategy.scenario)"></i>
          <span [style.color]="getScenarioColor(strategy.scenario)">{{ getScenarioLabel(strategy.scenario) }}</span>
        </div>

        <div class="timing-badge">
          <i class="fa-solid fa-clock"></i>
          <span>{{ formatDaysRange() }}</span>
        </div>

        <div class="channels-badge">
          <i class="fa-solid fa-tower-broadcast"></i>
          <span>{{ getActiveChannelsCount() }} canal(es) activo(s)</span>
        </div>

        <div *ngIf="strategy.autoExecute" class="auto-execute-badge">
          <i class="fa-solid fa-rotate"></i>
          <span>Ejecución automática</span>
        </div>
      </div>

      <div *ngIf="strategy.description" class="strategy-description">
        <i class="fa-solid fa-info-circle"></i>
        <p>{{ strategy.description }}</p>
      </div>
    </div>

    <!-- GRID DE INFORMACIÓN BÁSICA -->
    <div class="strategy-info-grid">
      <!-- CONFIGURACIÓN TEMPORAL -->
      <div class="card-style info-card">
        <div class="card-header">
          <i class="fa-solid fa-calendar-clock"></i>
          <h3>Configuración Temporal</h3>
        </div>
        <div class="card-content">
          <div class="config-section">
            <div class="config-item">
              <div class="config-label">
                <i class="pi pi-calendar"></i>
                <span>Escenario</span>
              </div>
              <div class="config-value scenario-value" [style.color]="getScenarioColor(strategy.scenario)">
                <i class="pi" [ngClass]="getScenarioIcon(strategy.scenario)"></i>
                <strong>{{ getScenarioLabel(strategy.scenario) }}</strong>
              </div>
            </div>

            <div class="config-item">
              <div class="config-label">
                <i class="fa-solid fa-arrows-left-right"></i>
                <span>Rango de días</span>
              </div>
              <div class="config-value">
                <strong>{{ formatDaysRange() }}</strong>
              </div>
            </div>

            <div class="config-item">
              <div class="config-label">
                <i class="fa-solid fa-rotate"></i>
                <span>Ejecución</span>
              </div>
              <div class="config-value">
                <span class="execution-badge" [ngClass]="strategy.autoExecute ? 'auto' : 'manual'">
                  <i [class]="strategy.autoExecute ? 'fa-solid fa-robot' : 'fa-solid fa-hand-pointer'"></i>
                  {{ strategy.autoExecute ? 'Automática' : 'Manual' }}
                </span>
              </div>
            </div>

            <div class="config-item">
              <div class="config-label">
                <i class="fa-solid fa-link"></i>
                <span>Plantilla maestra</span>
              </div>
              <div class="config-value">
                <code class="template-id">{{ getMasterTemplateName() }}</code>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- INFORMACIÓN DE AUDITORÍA -->
      <div class="card-style info-card">
        <div class="card-header">
          <i class="fa-solid fa-clock-rotate-left"></i>
          <h3>Auditoría</h3>
        </div>
        <div class="card-content">
          <div class="audit-timeline">
            <div class="audit-item">
              <div class="audit-marker created-marker">
                <i class="fa-solid fa-plus"></i>
              </div>
              <div class="audit-content">
                <div class="audit-label">Creada</div>
                <div class="audit-value">{{ formatDate(strategy.createdAt) }}</div>
              </div>
            </div>

            <div class="audit-item">
              <div class="audit-marker updated-marker">
                <i class="fa-solid fa-pen"></i>
              </div>
              <div class="audit-content">
                <div class="audit-label">Última actualización</div>
                <div class="audit-value">{{ formatDate(strategy.updatedAt) }}</div>
              </div>
            </div>

            <div *ngIf="strategy.deletedAt" class="audit-item">
              <div class="audit-marker deleted-marker">
                <i class="fa-solid fa-trash"></i>
              </div>
              <div class="audit-content">
                <div class="audit-label">Eliminada</div>
                <div class="audit-value">{{ formatDate(strategy.deletedAt) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SECCIÓN DE CANALES -->
    <div class="channels-section">
      <h2 class="section-title">
        <i class="fa-solid fa-tower-broadcast"></i>
        <span>Canales de Comunicación</span>
      </h2>

      <!-- EMAIL -->
      <div class="card-style channel-card email-card" *ngIf="isChannelEnabled('email')">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon email-icon">
              <i class="fa-solid fa-envelope"></i>
            </div>
            <h3>Email</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
        </div>
        <div class="channel-content">
          <div class="content-field">
            <div class="field-label">
              <i class="fa-solid fa-circle-check"></i>
              <span>Estado</span>
            </div>
            <div class="field-value">Canal habilitado y listo para enviar comunicaciones por correo electrónico</div>
          </div>
        </div>
      </div>

      <!-- SMS -->
      <div class="card-style channel-card sms-card" *ngIf="isChannelEnabled('sms')">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon sms-icon">
              <i class="fa-solid fa-comment-sms"></i>
            </div>
            <h3>SMS</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
        </div>
        <div class="channel-content">
          <div class="content-field">
            <div class="field-label">
              <i class="fa-solid fa-circle-check"></i>
              <span>Estado</span>
            </div>
            <div class="field-value">Canal habilitado y listo para enviar mensajes de texto SMS</div>
          </div>
        </div>
      </div>

      <!-- WHATSAPP -->
      <div class="card-style channel-card whatsapp-card" *ngIf="isChannelEnabled('whatsapp')">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon whatsapp-icon">
              <i class="fa-brands fa-whatsapp"></i>
            </div>
            <h3>WhatsApp</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
        </div>
        <div class="channel-content">
          <div class="content-field">
            <div class="field-label">
              <i class="fa-solid fa-circle-check"></i>
              <span>Estado</span>
            </div>
            <div class="field-value">Canal habilitado y listo para enviar mensajes por WhatsApp</div>
          </div>
        </div>
      </div>

      <!-- LLAMADA -->
      <div class="card-style channel-card call-card" *ngIf="isChannelEnabled('call')">
        <div class="channel-header">
          <div class="channel-title">
            <div class="channel-icon call-icon">
              <i class="fa-solid fa-phone"></i>
            </div>
            <h3>Llamada telefónica</h3>
            <span class="channel-badge configured-badge">
              <i class="fa-solid fa-check"></i>
              Configurado
            </span>
          </div>
        </div>
        <div class="channel-content">
          <div class="content-field">
            <div class="field-label">
              <i class="fa-solid fa-circle-check"></i>
              <span>Estado</span>
            </div>
            <div class="field-value">Canal habilitado y listo para realizar llamadas telefónicas</div>
          </div>
        </div>
      </div>

      <!-- CANALES NO CONFIGURADOS -->
      <div class="not-configured-channels">
        <div *ngIf="!isChannelEnabled('email')" class="not-configured-item">
          <i class="fa-solid fa-envelope"></i>
          <span>Email no configurado</span>
        </div>
        <div *ngIf="!isChannelEnabled('sms')" class="not-configured-item">
          <i class="fa-solid fa-comment-sms"></i>
          <span>SMS no configurado</span>
        </div>
        <div *ngIf="!isChannelEnabled('whatsapp')" class="not-configured-item">
          <i class="fa-brands fa-whatsapp"></i>
          <span>WhatsApp no configurado</span>
        </div>
        <div *ngIf="!isChannelEnabled('call')" class="not-configured-item">
          <i class="fa-solid fa-phone"></i>
          <span>Llamada no configurada</span>
        </div>
      </div>
    </div>
  </ng-container>
</div>

<!-- Modal para Ver Leases Elegibles -->
<p-dialog
  [(visible)]="showLeasesModal"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [style]="{ width: '90vw', maxWidth: '1400px' }"
  header="Leases Elegibles para esta Estrategia"
  (onHide)="hideLeasesModal()">
  <app-lease-selector
    *ngIf="showLeasesModal"
    [leases]="eligibleLeases"
    (onCancel)="hideLeasesModal()">
  </app-lease-selector>
</p-dialog>

<!-- Modal para Editar Estrategia -->
<p-dialog
  [(visible)]="showEditModal"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [style]="{ width: '90vw', maxWidth: '1200px' }"
  header="Editar Estrategia"
  (onHide)="hideEditModal()">
  <app-strategy-wizard
    *ngIf="showEditModal"
    (onSave)="saveForm()"
    (onCancel)="hideEditModal()">
  </app-strategy-wizard>
</p-dialog>

<!-- Toast para notificaciones -->
<p-toast></p-toast>
